import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { ErrorProvider } from './contexts/ErrorContext';
import { CartProvider } from './contexts/CartContext';
import { useSupabase } from './contexts/SupabaseContext';
import { initializeSecurity } from './lib/security';
import { initializePerformanceMonitoring } from './lib/performance';
import { GoogleAnalytics } from './lib/analytics';
import { logger } from './lib/logger';
import { AlertTriangle, X, Loader } from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';
import DevelopmentPopup from './components/DevelopmentPopup';
import SupabaseConnectionStatus from './components/SupabaseConnectionStatus';
import AdminLayout from './pages/admin/AdminLayout';
import SellerLayout from './pages/seller/SellerLayout';
import WarehouseLayout from './pages/warehouse/WarehouseLayout';
import HomePage from './pages/HomePage';
import MerchantAcquisitionForm from './pages/admin/MerchantAcquisitionForm';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import SellerStorePage from './pages/SellerStorePage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierStorePage from './pages/SupplierStorePage';
import SignInPage from './pages/SignInPage';
import CreateAccountPage from './pages/CreateAccountPage';
import BecomeExclusivePage from './pages/BecomeExclusivePage';
import { SupabaseProvider } from './contexts/SupabaseContext';
import AdminLogin from './pages/admin/AdminLogin';
import AdminAuthGuard from './components/AdminAuthGuard';
import SellerAuthGuard from './components/SellerAuthGuard';
import Settings from './pages/admin/Settings';
import ErrorLog from './pages/admin/ErrorLog';
import SellerLogin from './pages/SellerLogin';
import CheckoutPage from './pages/CheckoutPage';
import EmailTemplatesPage from './pages/admin/EmailTemplatesPage';
import CartPage from './pages/CartPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';

// Dynamic Category Page
import DynamicCategoryPage from './pages/DynamicCategoryPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import CategoryManagement from './pages/admin/CategoryManagement';
import ProductManagement from './pages/admin/ProductManagement';
import OrderManagement from './pages/admin/OrderManagement';
import SupplierManagement from './pages/admin/SupplierManagement';
import PaymentManagement from './pages/admin/PaymentManagement';
import MessageManagement from './pages/admin/MessageManagement';
import AllUrlsPage from './pages/admin/AllUrlsPage';
import BlogManagement from './pages/admin/BlogManagement';

// Seller Central Pages
import SellerDashboard from './pages/seller/SellerDashboard';
import SellerProducts from './pages/seller/SellerProducts';
import SellerVideos from './pages/seller/SellerVideos';
import SellerOrders from './pages/seller/SellerOrders';
import SellerInventory from './pages/seller/SellerInventory';
import SellerAnalytics from './pages/seller/SellerAnalytics';
import SellerProfile from './pages/seller/SellerProfile';
import SellerVariantsPage from './pages/seller/SellerVariantsPage';
import SellerDocumentPreparationPage from './pages/seller/SellerDocumentPreparationPage';
import SellerAddProduct from './pages/seller/SellerAddProduct';

// Warehouse Pages
import WarehouseDashboard from './pages/warehouse/WarehouseDashboard';
import InventoryManagement from './pages/warehouse/InventoryManagement';
import ShippingManagement from './pages/warehouse/ShippingManagement';
import ReceivingManagement from './pages/warehouse/ReceivingManagement';
import WarehouseReports from './pages/warehouse/WarehouseReports';

// Buyer Pages
import BuyerDocumentsPage from './pages/buyer/BuyerDocumentsPage';
import BuyerProfilePage from './pages/buyer/BuyerProfilePage';

// RFQ Pages
import RFQPage from './pages/RFQPage';
import MyRFQsPage from './pages/MyRFQsPage';
import RFQDetailPage from './pages/RFQDetailPage';

// Supplier Onboarding
import SupplierOnboardingPage from './pages/SupplierOnboardingPage';

// Global Error and Loading Component
const GlobalStatusDisplay = () => {
  const { globalError, setGlobalError, loading } = useSupabase();
  
  if (!globalError && !loading) return null;
  
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      {globalError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{globalError}</span>
          </div>
          <button 
            onClick={() => setGlobalError(null)}
            className="text-red-500 hover:text-red-700 ml-4"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      
      {loading && (
        <div className="bg-white border border-gray-200 px-4 py-3 rounded-lg shadow-lg flex items-center mt-2">
          <Loader className="h-5 w-5 text-orange-500 animate-spin mr-2" />
          <span className="text-gray-700">Loading...</span>
        </div>
      )}
    </div>
  );
};

// ScrollToTop component to reset scroll position on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view with Google Analytics
    GoogleAnalytics.trackPageView(pathname);
  }, [pathname]);

  return null;
};

// Component to conditionally render header based on current route
const ConditionalHeader = () => {
  const location = useLocation();
  
  // Hide header for admin, seller-central, and warehouse routes
  const hideHeader = location.pathname.startsWith('/admin') || 
                    location.pathname.startsWith('/seller-central') || 
                    location.pathname.startsWith('/warehouse');
  
  if (hideHeader) {
    return null;
  }
  
  return <Header />;
};

// Component to conditionally render footer based on current route
const ConditionalFooter = () => {
  const location = useLocation();
  
  // Hide footer for admin, seller-central, and warehouse routes
  const hideFooter = location.pathname.startsWith('/admin') || 
                    location.pathname.startsWith('/seller-central') || 
                    location.pathname.startsWith('/warehouse');
  
  if (hideFooter) {
    return null;
  }
  
  return <Footer />;
};

function App() {
  // Initialize security and performance monitoring
  useEffect(() => {
    try {
      initializeSecurity();
      initializePerformanceMonitoring();
      GoogleAnalytics.init();
      logger.info('Application initialized successfully', 'App');
    } catch (error) {
      logger.error('Failed to initialize application', 'App', { error });
    }
  }, []);

  return (
    <ErrorProvider>
      <SupabaseProvider>
        <CartProvider>
          <ErrorBoundary>
            <Router>
              <ScrollToTop />
              <div className="min-h-screen flex flex-col">
                <ConditionalHeader />
                <main className="flex-1">
                  <Routes>
                    {/* Admin Login */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    
                    {/* Seller Login */}
                    <Route path="/seller-login" element={<SellerLogin />} />

                    {/* Admin Routes - Protected */}
                    <Route path="/admin/*" element={
                      <AdminAuthGuard>
                        <AdminLayout>
                          <Routes>
                            <Route index element={<AdminDashboard />} />
                            <Route path="users" element={<UserManagement />} />
                            <Route path="categories" element={<CategoryManagement />} />
                            <Route path="products" element={<ProductManagement />} />
                            <Route path="orders" element={<OrderManagement />} />
                            <Route path="videos" element={<SellerVideos />} />
                            <Route path="suppliers" element={<SupplierManagement />} />
                            <Route path="payments" element={<PaymentManagement />} />
                            <Route path="messages" element={<MessageManagement />} />
                            <Route path="blogs" element={<BlogManagement />} />
                            <Route path="email-templates" element={<EmailTemplatesPage />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="error-log" element={<ErrorLog />} />
                            <Route path="all-urls" element={<AllUrlsPage />} />
                            <Route path="merchant-acquisition" element={<MerchantAcquisitionForm />} />
                          </Routes>
                        </AdminLayout>
                      </AdminAuthGuard>
                    } />

                    {/* Seller Central Routes */}
                    <Route path="/seller-central/*" element={
                      <SellerAuthGuard>
                        <SellerLayout>
                          <Routes>
                            <Route index element={<SellerDashboard />} />
                            <Route path="products" element={<SellerProducts />} />
                            <Route path="products/add" element={<SellerAddProduct />} />
                            <Route path="products/:productId/variants" element={<SellerVariantsPage />} />
                            <Route path="videos" element={<SellerVideos />} />
                            <Route path="orders" element={<SellerOrders />} />
                            <Route path="inventory" element={<SellerInventory />} />
                            <Route path="analytics" element={<SellerAnalytics />} />
                            <Route path="profile" element={<SellerProfile />} />
                            <Route path="documents/prepare" element={<SellerDocumentPreparationPage />} />
                          </Routes>
                        </SellerLayout>
                      </SellerAuthGuard>
                    } />

                    {/* Warehouse Management Routes */}
                    <Route path="/warehouse/*" element={
                      <WarehouseLayout>
                        <Routes>
                          <Route index element={<WarehouseDashboard />} />
                          <Route path="inventory" element={<InventoryManagement />} />
                          <Route path="shipping" element={<ShippingManagement />} />
                          <Route path="receiving" element={<ReceivingManagement />} />
                          <Route path="reports" element={<WarehouseReports />} />
                        </Routes>
                      </WarehouseLayout>
                    } />
                    
                    {/* Public Routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/product/:id" element={<ProductDetailPage />} />
                    <Route path="/category/:category" element={<ProductsPage />} />
                    
                    {/* Dynamic Category Page */}
                    <Route path="/category/:categorySlug" element={<DynamicCategoryPage />} />
                    
                    <Route path="/seller/:sellerSlug" element={<SellerStorePage />} />
                    <Route path="/suppliers" element={<SuppliersPage />} />
                    <Route path="/supplier/:supplierSlug" element={<SupplierStorePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/help" element={<ContactPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/services" element={<ContactPage />} />
                    <Route path="/merchant-acquisition" element={<MerchantAcquisitionForm />} />
                    <Route path="/signin" element={<SignInPage />} />
                    <Route path="/create-account" element={<CreateAccountPage />} />
                    <Route path="/become-exclusive" element={<BecomeExclusivePage />} />
                    <Route path="/my-documents" element={<BuyerDocumentsPage />} />
                    <Route path="/profile" element={<BuyerProfilePage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
                    <Route path="/orders" element={<BuyerProfilePage />} />
                    <Route path="/wishlist" element={<BuyerProfilePage />} />
                    <Route path="/messages" element={<BuyerProfilePage />} />
                    
                    {/* RFQ Routes */}
                    <Route path="/rfq/create" element={<RFQPage />} />
                    <Route path="/my-rfqs" element={<MyRFQsPage />} />
                    <Route path="/rfq/:id" element={<RFQDetailPage />} />
                    
                    {/* Supplier Onboarding */}
                    <Route path="/supplier-onboarding" element={<SupplierOnboardingPage />} />
                  </Routes>
                </main>
                <ConditionalFooter />
                <FloatingButtons />
                <GlobalStatusDisplay />
                <DevelopmentPopup />
                <SupabaseConnectionStatus />
              </div>
            </Router>
          </ErrorBoundary>
        </CartProvider>
      </SupabaseProvider>
    </ErrorProvider>
  );
}

export default App;